import { useState } from 'react';
import ConsultationModal from './ConsultationModal';

interface HeroSectionProps {
  onCTAClick?: () => void;
  onLearnMore?: () => void;
}

export default function HeroSection({ onCTAClick, onLearnMore }: HeroSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDemoClick = () => {
    setIsModalOpen(true);
    if (onCTAClick) onCTAClick();
  };

  return (
    <>
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#0A0F1C' }}
      >
        {/* ── 背景：深邃科技光暈 (純 CSS，無圖片) ── */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* 正中央主光暈 */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              width: '900px',
              height: '600px',
              background: 'radial-gradient(ellipse at center, rgba(0,180,255,0.09) 0%, rgba(0,120,220,0.05) 40%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          {/* 左上輔助光暈 */}
          <div
            className="absolute -top-20 -left-20 rounded-full"
            style={{
              width: '500px',
              height: '500px',
              background: 'radial-gradient(ellipse, rgba(0,160,240,0.06) 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
          {/* 右下輔助光暈 */}
          <div
            className="absolute -bottom-20 -right-20 rounded-full"
            style={{
              width: '600px',
              height: '600px',
              background: 'radial-gradient(ellipse, rgba(212,175,55,0.07) 0%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          />
          {/* 極細科技網格線 */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'linear-gradient(rgba(0,180,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,180,255,0.03) 1px, transparent 1px)',
              backgroundSize: '80px 80px',
            }}
          />
          {/* 頂部微光邊 */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(0,180,255,0.3), transparent)' }}
          />
        </div>

        {/* ── 主體內容：單欄置中 ── */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-32 sm:py-40 flex flex-col items-center text-center">

          {/* 頂部黃色小標籤 */}
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-8 sm:mb-10">
            <i className="ri-award-fill"></i>
            <span>企業級 O2O ERP 管理模組 · 全鏈路獲客閉環</span>
          </div>

          {/* 主標題 Hero Text */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white leading-tight tracking-tight mb-6 sm:mb-8">
            企業級數位
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #D4AF37 0%, #F0C040 40%, #FFD966 70%, #D4AF37 100%)',
              }}
            >
              自動印鈔引擎
            </span>
          </h1>

          {/* 副標題 */}
          <p
            className="text-lg sm:text-xl text-gray-400 leading-relaxed mb-10 sm:mb-12"
            style={{ maxWidth: '800px' }}
          >
            防呆管理與精準流量的完美融合。我們將頂級廣告追蹤技術、O2O 雙核展間架構與 ERP 自動化管理熔於一爐，為高單價實體門市打造全自動獲客與成交閉環。
          </p>

          {/* CTA 按鈕組 — 並排置中 */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-14 sm:mb-16">
            <button
              onClick={handleDemoClick}
              className="group relative px-8 py-4 font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #F0C040 100%)',
                color: '#060F1C',
              }}
            >
              <span className="relative z-10">預約免費系統展示 (Book Your Demo)</span>
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'linear-gradient(135deg, #F0C040 0%, #FFD966 100%)' }}
              />
            </button>

            {onLearnMore && (
              <button
                onClick={onLearnMore}
                className="px-8 py-4 font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(0,180,255,0.4)',
                  color: '#64D2FF',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,180,255,0.08)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,180,255,0.7)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,180,255,0.4)';
                }}
              >
                了解解決方案
              </button>
            )}
          </div>

          {/* 分隔線 */}
          <div
            className="w-full mb-10 sm:mb-12"
            style={{
              maxWidth: '480px',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            }}
          />

          {/* 數據指標 — 橫向並排置中 */}
          <div className="flex flex-row items-start justify-center gap-8 sm:gap-16">
            <div className="flex flex-col items-center">
              <div
                className="text-3xl sm:text-4xl font-black mb-1 bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #D4AF37, #F0C040)' }}
              >
                O2O
              </div>
              <div className="text-xs sm:text-sm text-gray-500 tracking-wide">全鏈路管理閉環</div>
            </div>

            <div
              className="w-px self-stretch"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            />

            <div className="flex flex-col items-center">
              <div
                className="text-3xl sm:text-4xl font-black mb-1 bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #D4AF37, #F0C040)' }}
              >
                GTM
              </div>
              <div className="text-xs sm:text-sm text-gray-500 tracking-wide">數據雷達追蹤</div>
            </div>

            <div
              className="w-px self-stretch"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            />

            <div className="flex flex-col items-center">
              <div
                className="text-3xl sm:text-4xl font-black mb-1 bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #D4AF37, #F0C040)' }}
              >
                24H
              </div>
              <div className="text-xs sm:text-sm text-gray-500 tracking-wide">顧問快速回覆</div>
            </div>
          </div>
        </div>

        {/* 底部滾動提示 */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-700 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#D4AF37] rounded-full"></div>
          </div>
        </div>
      </section>

      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
